<script setup lang="ts">
import DisqualifyAfterSubmit from "@/components/common/OptionsSettings/DisqualifySettings/DisqualifyAfterSubmit.vue";
import DisqualifyImmediately from "@/components/common/OptionsSettings/DisqualifySettings/DisqualifyImmediately.vue";
import FacebookPixelEvent from "@/components/common/OptionsSettings/Facebook/FacebookPixelEvent.vue";
import FacebookPixelId from "@/components/common/OptionsSettings/Facebook/FacebookPixelId.vue";
import MessageStyling from "@/components/common/OptionsSettings/MessageStyling.vue";
import RichTextEditor from "@/components/common/RichTextEditor.vue";
import { useAppStore } from "@/store/app";
import { PRODUCT_TYPE, tooltipMessage } from "@/util/constants";
import { isTrueCustomObjectField } from "@/util/customObjectUtils";
import { UIAlert, UICard, UIDivider, UIInput, UISelect, UISwitch } from "@gohighlevel/ghl-ui";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const appStore = useAppStore();

// Modal state for one question at a time
const openAlertModal = ref(false);
const pendingToggleValue = ref(false);

// Utility to inject Google Font weights once per family
const loadFont = (family: string): void => {
  if (!family) {
    return;
  }
  const id = `dynamic-font-${family}`;
  if (document.getElementById(id)) {
    return;
  }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  const sanitized = family.replace(/ /g, "+");
  // Load common weights so weight changes reflect without new requests
  link.href = `https://fonts.googleapis.com/css2?family=${sanitized}:wght@100;300;400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
};

// Initialize the actionType to "2" (message) if it's not already set
onMounted(() => {
  // For quiz products, always default to quizResult if actionType is not already set to quizResult
  if (appStore.product === PRODUCT_TYPE.QUIZ) {
    if (!appStore.formAction.actionType) {
      appStore.formAction.actionType = "4"; // Default to quizResult for quiz
    }
  } else if (!appStore.formAction.actionType) {
    // For other products, default to message only if actionType is not set
    appStore.formAction.actionType = "2"; // Default to message for other products
  }

  // Initialize isEnabled if needed
  if (appStore.submitMessageStyle.isEnabled === undefined) {
    appStore.submitMessageStyle.isEnabled = false;
  }

  // Initial font load
  loadFont(appStore.submitMessageStyle.fontFamily);
});

// Reload font file if family changes
watch(
  () => appStore.submitMessageStyle.fontFamily,
  (newFont) => {
    loadFont(newFont);
  }
);

// Message content and type
const messageType = computed({
  get: () => {
    const actionType = String(appStore.formAction?.actionType);
    if (actionType === "1") {
      return "url";
    }
    if (actionType === "2") {
      return "message";
    }
    if (actionType === "3") {
      return "order";
    }
    if (actionType === "4") {
      return "quizResult";
    }
    return "message"; // Default to message
  },
  set: (value) => {
    if (value === "url") {
      appStore.formAction.actionType = "1";
    } else if (value === "message") {
      appStore.formAction.actionType = "2";
    } else if (value === "order") {
      appStore.formAction.actionType = "3";
    } else if (value === "quizResult") {
      appStore.formAction.actionType = "4";
    }
    appStore.anyUnsavedChanges = true;
    appStore.addToHistory();
  },
});

const messageContent = computed({
  get: () => appStore.formAction.thankyouText,
  set: (value) => {
    appStore.formAction.thankyouText = value;
    appStore.anyUnsavedChanges = true;
  },
});

// Message styling from the store
const messageStyle = computed(() => appStore.submitMessageStyle);

// Check if styling is enabled from the store
const isStylingEnabled = computed(() => !!messageStyle.value.isEnabled);

// Dynamic inline styles for the preview text
const previewTextStyle = computed(() => {
  // Only apply custom styling when message styling toggle is on
  return {
    color: `#${messageStyle.value.color}`,
    fontFamily: messageStyle.value.fontFamily,
    fontSize: `${messageStyle.value.fontSize}px`,
    fontWeight: messageStyle.value.fontWeight,
    backgroundColor: `#${messageStyle.value.bgColor}`,
  };
  // Return default styling when toggle is off
  return {};
});

const redirectUrl = computed({
  get: () => appStore.formAction.redirectUrl,
  set: (value) => {
    appStore.formAction.redirectUrl = value;
    appStore.anyUnsavedChanges = true;
  },
});

// Disable partial contact creation when custom object or business fields exist
const hasCOOrBusinessInSurvey = computed<boolean>(() => {
  if (appStore.product !== PRODUCT_TYPE.SURVEY) {
    return false;
  }
  const slides = appStore.slides || [];
  return slides.some((slide: any) =>
    (slide.slideData || []).some(
      (el: any) => isTrueCustomObjectField(el?.model) || el?.model === "business"
    )
  );
});

// Ensure it's turned off if currently on
watch(
  hasCOOrBusinessInSurvey,
  (shouldDisable) => {
    if (shouldDisable && appStore.builderConfig.enablePartialContactCreation) {
      appStore.builderConfig.enablePartialContactCreation = false;
      appStore.anyUnsavedChanges = true;
      appStore.addToHistory();
    }
  },
  { immediate: true }
);

// Form Settings
const enableStickyContact = computed(() => appStore.builderConfig.stickyContact || false);
const enableTimezone = computed(() => appStore.builderConfig.enableTimezone || false);
const enableGDPRFont = computed(() => appStore.builderConfig.isGDPRCompliant || false);

// Dynamic message type options based on product
const messageTypeOptions = computed(() => {
  const options = [
    { label: t("common.openUrl"), value: "url" },
    { label: t("common.message"), value: "message" },
    {
      label: t("common.payment.product.orderConfirmation"),
      value: "order",
      disabled: appStore.product === PRODUCT_TYPE.QUIZ,
    },
  ];

  // Add quiz result option for quiz products
  if (appStore.product === PRODUCT_TYPE.QUIZ) {
    options.push({ label: t("quiz.quizResult.resultTemplate"), value: "quizResult" });
  }

  return options;
});

// Common handler function for all toggle settings
const updateSetting = (settingKey: string, value: boolean) => {
  appStore.builderConfig[settingKey] = value;
  appStore.anyUnsavedChanges = true;
  appStore.addToHistory();
};

// Guarded updater for partial contact creation
const updatePartialContactSetting = (value: boolean) => {
  if (hasCOOrBusinessInSurvey.value) {
    if (appStore.builderConfig.enablePartialContactCreation) {
      appStore.builderConfig.enablePartialContactCreation = false;
      appStore.anyUnsavedChanges = true;
      appStore.addToHistory();
    }
    return;
  }
  updateSetting("enablePartialContactCreation", value);
};

// Handler for auto navigation (inverted value)
const updateAutoNavigation = (value: boolean) => {
  // The value is inverted - disableAutoNavigation = !value
  appStore.builderConfig.disableAutoNavigation = !value;
  appStore.anyUnsavedChanges = true;
  appStore.addToHistory();
};
</script>

<template>
  <div class="settings-container">
    <div class="settings-wrapper">
      <!-- On Submit Section -->
      <UICard class="mb-6">
        <template #header>
          <div class="border-b border-gray-200">
            <h3 class="text-lg font-semibold">{{ t("common.onSubmit") }}</h3>
          </div>
        </template>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2" style="align-items: start">
          <!-- Left Column: Settings -->
          <div>
            <p v-if="appStore.builderConfig?.conditionalLogic?.length" class="mb-3">
              <UIAlert id="warning-message" type="warning">
                <template #content>{{ t("common.conditionalLogicAlertOnSubmit") }}</template>
              </UIAlert>
            </p>

            <UISelect
              id="submit"
              :value="messageType"
              :options="messageTypeOptions"
              @update:value="messageType = $event"
            />

            <UIInput
              v-if="messageType === 'url'"
              id="submitMessage"
              :model-value="redirectUrl"
              :placeholder="redirectUrl || t('common.openUrl')"
              class="my-2"
              @update:model-value="redirectUrl = $event"
              @blur="appStore.addToHistory()"
            >
            </UIInput>

            <div v-if="messageType === 'url'" class="small-alert mt-2">
              <UIAlert id="info-message" type="info">
                <template #content>
                  {{ t("common.addUrlLike") }} <b>example.com</b>, <b>https://example.com</b>,
                  <b>example.com/path</b> {{ t("common.or") }} <b>https://www.example.com/path</b>.
                </template>
              </UIAlert>
            </div>

            <div v-if="messageType === 'order'" class="small-alert mt-2">
              <UIAlert id="info-message" type="info">
                <template #content>{{
                  t("common.payment.product.orderConfirmationWarning")
                }}</template>
              </UIAlert>
            </div>

            <div v-if="messageType === 'quizResult'" class="small-alert mt-2">
              <UIAlert id="info-message" type="info">
                <template #content>{{ t("quiz.quizResult.quizResultAlertMessage") }}</template>
              </UIAlert>
            </div>

            <section v-if="messageType === 'message'" class="w-100 mt-3 flex flex-col gap-3">
              <RichTextEditor
                v-model:modelValue="messageContent"
                :is-submit-message="true"
              ></RichTextEditor>
              <MessageStyling></MessageStyling>
            </section>
          </div>

          <!-- Right Column: Preview -->
          <div v-if="messageType === 'message'" class="preview-container">
            <div class="mb-2 text-sm font-medium text-gray-600">
              {{ t("common.preview") }}
            </div>
            <div
              class="bg-container relative flex h-full items-center justify-center overflow-hidden rounded-xl p-8"
            >
              <div
                class="z-10 w-full rounded-lg bg-white px-4 py-20 text-center"
                :style="isStylingEnabled ? { backgroundColor: `#${messageStyle.bgColor}` } : {}"
              >
                <div
                  class="message-content line-clamp-3 max-h-[100px] overflow-hidden text-ellipsis break-words"
                  :style="previewTextStyle"
                  v-html="messageContent"
                ></div>
              </div>
            </div>
          </div>

          <!-- URL Preview -->
          <div v-if="messageType === 'url'" class="preview-container">
            <div class="mb-2 text-sm font-medium text-gray-600">
              {{ t("common.preview") }}
            </div>
            <div
              class="bg-container relative flex h-full items-center justify-center overflow-hidden rounded-xl p-8"
            >
              <div class="z-10 w-full rounded-lg bg-white px-4 py-8 text-center">
                <div class="mb-4 flex justify-center">
                  <img src="@/assets/img/export.png" alt="Export" class="h-12 w-12" />
                </div>
                <div class="mb-4 font-medium">
                  {{ t("common.redirectMessage") }}
                </div>
                <div class="px-2">
                  <UIInput
                    id="redirect-url-preview"
                    :model-value="redirectUrl"
                    placeholder="https://example.com"
                    class="w-full"
                    size="small"
                    @update:model-value="redirectUrl = $event"
                    @blur="appStore.addToHistory()"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Order Confirmation Preview -->
          <div v-if="messageType === 'order'" class="preview-container">
            <div class="mb-2 text-sm font-medium text-gray-600">
              {{ t("common.preview") }}
            </div>
            <div
              class="bg-container relative flex h-full items-center justify-center overflow-hidden rounded-xl p-8"
            >
              <div class="z-10 w-full rounded-lg bg-white px-4 py-12 pt-20 text-center">
                <div class="mb-3 flex justify-center">
                  <img src="@/assets/img/shipment.png" alt="Shipment" class="h-12 w-12" />
                </div>
                <div class="mb-4 font-medium">
                  {{ t("common.payment.product.orderConfirmation") }}
                </div>
              </div>
            </div>
          </div>

          <!-- Quiz Result Preview -->
          <div v-if="messageType === 'quizResult'" class="preview-container">
            <div class="mb-2 text-sm font-medium text-gray-600">
              {{ t("common.preview") }}
            </div>
            <div
              class="bg-container relative flex h-full items-center justify-center overflow-hidden rounded-xl p-8"
            >
              <div class="z-10 w-full rounded-lg bg-white px-4 py-8 text-center">
                <div class="mb-3 flex justify-center">
                  <img
                    src="@/assets/img/text-file--text-common-file.png"
                    alt="Result Page"
                    class="h-12 w-10"
                  />
                </div>
                <div class="mb-2 text-lg font-semibold">
                  {{ t("quiz.quizResult.resultTemplate") }}
                </div>
                <div class="text-sm text-gray-600">
                  {{ t("quiz.quizResult.quizResultAlertMessage") }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </UICard>

      <!-- Disqualify Settings Section (Survey Only) -->
      <UICard
        v-if="appStore.product === PRODUCT_TYPE.SURVEY && (appStore.builder?.version ?? 0) < 4"
        class="mb-6"
      >
        <template #header>
          <div class="border-b border-gray-200">
            <h3 class="text-lg font-semibold">{{ t("survey.disqualifySettings") }}</h3>
          </div>
        </template>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2" style="align-items: start">
          <!-- Left Column: Disqualify Immediately -->
          <div>
            <p class="mb-3 text-sm font-medium text-gray-700">
              {{ t("survey.disqualifyImmediatelyLabel") }}
            </p>
            <DisqualifyImmediately />
          </div>

          <!-- Right Column: Disqualify After Submit -->
          <div>
            <p class="mb-3 text-sm font-medium text-gray-700">
              {{ t("survey.disqualifyAfterSubmitLabel") }}
            </p>
            <DisqualifyAfterSubmit />
          </div>
        </div>
      </UICard>

      <!-- Facebook Pixel ID Section -->
      <UICard class="mb-6">
        <template #header>
          <div class="border-b border-gray-200">
            <h3 class="text-lg font-semibold">{{ t("common.facebookPixelID") }}</h3>
          </div>
        </template>

        <div class="space-y-4">
          <FacebookPixelId />
        </div>
      </UICard>

      <!-- Facebook Pixel Events Section -->
      <UICard class="mb-6">
        <template #header>
          <div class="border-b border-gray-200">
            <h3 class="text-lg font-semibold">{{ t("common.facebookPixelEvents") }}</h3>
          </div>
        </template>

        <div>
          <FacebookPixelEvent :product="PRODUCT_TYPE.FORM" />
        </div>
      </UICard>

      <!-- Form Settings Section -->
      <UICard class="mb-6">
        <template #header>
          <div class="border-b border-gray-200">
            <h3 class="text-lg font-semibold">
              {{
                appStore.product === PRODUCT_TYPE.FORM
                  ? t("form.formSettings")
                  : appStore.product === PRODUCT_TYPE.SURVEY
                  ? t("survey.surveySettings")
                  : t("quiz.quizSettings")
              }}
            </h3>
          </div>
        </template>

        <div class="space-y-6">
          <!-- Survey-specific settings -->
          <template v-if="appStore.product === PRODUCT_TYPE.SURVEY">
            <!-- Create Contact on Partial Submission (Survey) -->
            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-partial-contact-survey"
                :value="appStore.builderConfig.enablePartialContactCreation"
                :disabled="hasCOOrBusinessInSurvey"
                class="mt-1 flex-shrink-0"
                @update:value="updatePartialContactSetting($event)"
              />
              <div>
                <p class="font-medium text-gray-700">
                  {{ t("common.createContactOnPartialSubmission") }}
                </p>
                <p class="mt-1 text-sm text-gray-500">
                  {{ t("survey.createContactOnPartialSubmissionSubText") }}
                </p>
              </div>
            </div>

            <UIDivider />
            <div v-if="!appStore?.builderConfig?.newFooter" class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-back-button"
                :value="appStore.builderConfig.isBackButtonEnable"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isBackButtonEnable', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.backButton") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.backButton }}</p>
              </div>
            </div>

            <UIDivider v-if="!appStore?.builderConfig?.newFooter" />

            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-auto-navigation"
                :value="!appStore.builderConfig.disableAutoNavigation"
                class="mt-1 flex-shrink-0"
                @update:value="updateAutoNavigation($event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.autoNavigation") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.autoNavigation }}</p>
              </div>
            </div>

            <UIDivider />

            <div v-if="!appStore?.builderConfig?.newFooter" class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-progress-bar"
                :value="appStore.builderConfig.isProgressBarEnabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isProgressBarEnabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.progressBar") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.progressBar }}</p>
              </div>
            </div>

            <UIDivider v-if="!appStore?.builderConfig?.newFooter" />

            <div v-if="!appStore?.builderConfig?.newFooter" class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-disable-animation"
                :value="appStore.builderConfig.isAnimationDisabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isAnimationDisabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.disableAnimation") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.disableAnimation }}</p>
              </div>
            </div>

            <UIDivider v-if="!appStore?.builderConfig?.newFooter" />

            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-survey-scroll"
                :value="appStore.builderConfig.isSurveyScrollEnabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isSurveyScrollEnabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.scrollToTop") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.scrollToTop }}</p>
              </div>
            </div>

            <UIDivider />
          </template>

          <!-- Quiz-specific settings -->
          <template v-else-if="appStore.product === PRODUCT_TYPE.QUIZ">
            <!-- Back Button (Quiz) -->
            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-back-button-quiz"
                :value="appStore.builderConfig.isBackButtonEnable"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isBackButtonEnable', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.backButton") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.backButton }}</p>
              </div>
            </div>

            <UIDivider />

            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-disable-animation-quiz"
                :value="appStore.builderConfig.isAnimationDisabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isAnimationDisabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.disableAnimation") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.disableAnimation }}</p>
              </div>
            </div>

            <UIDivider />

            <div class="flex items-start gap-4">
              <UISwitch
                id="settings-switch-survey-scroll-quiz"
                :value="appStore.builderConfig.isSurveyScrollEnabled"
                class="mt-1 flex-shrink-0"
                @update:value="updateSetting('isSurveyScrollEnabled', $event)"
              />
              <div>
                <p class="font-medium text-gray-700">{{ t("survey.scrollToTop") }}</p>
                <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.scrollToTop }}</p>
              </div>
            </div>

            <UIDivider />
          </template>

          <!-- Common settings for all product types -->
          <div class="flex items-start gap-4">
            <UISwitch
              id="settings-switch-sticky-contact"
              :value="enableStickyContact"
              class="mt-1 flex-shrink-0"
              @update:value="updateSetting('stickyContact', $event)"
            />
            <div>
              <p class="font-medium text-gray-700">{{ t("common.stickyContact") }}</p>
              <p class="mt-1 text-sm text-gray-500">{{ tooltipMessage.stickyContact }}</p>
            </div>
          </div>

          <UIDivider />

          <div class="flex items-start gap-4">
            <UISwitch
              id="settings-switch-timezone"
              :value="enableTimezone"
              class="mt-1 flex-shrink-0"
              @update:value="updateSetting('enableTimezone', $event)"
            />
            <div>
              <p class="font-medium text-gray-700">{{ t("common.enableTimezone") }}</p>
              <p class="mt-1 text-sm text-gray-500">
                {{
                  appStore.product === PRODUCT_TYPE.FORM
                    ? tooltipMessage.enableTimezoneForm
                    : tooltipMessage.enableTimezoneSurvey
                }}
              </p>
            </div>
          </div>

          <UIDivider />

          <div class="flex items-start gap-4">
            <UISwitch
              id="settings-switch-gdpr-font"
              :value="enableGDPRFont"
              class="mt-1 flex-shrink-0"
              @update:value="updateSetting('isGDPRCompliant', $event)"
            />
            <div>
              <p class="font-medium text-gray-700">{{ t("common.useGDPRCompliantFont") }}</p>
            </div>
          </div>
        </div>
      </UICard>
    </div>
  </div>
</template>

<style scoped>
.settings-container {
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: 1.5rem;
  background-color: #f2f4f7;
  display: flex;
  flex-direction: column;
}

.settings-wrapper {
  width: 850px;
  max-width: 850px;
  margin: 0 auto;
  flex: 1;
  padding-bottom: 100px;
}

.small-alert {
  font-size: 0.875rem;
}

.preview-container {
  height: 300px !important;
  min-height: 300px !important;
  max-height: 300px !important;
  padding-bottom: 36px;
}

.message-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  text-overflow: ellipsis;
}

.bg-container {
  background-image: url("@/assets/img/message-background.png");
  background-size: cover;
  background-position: center;
  position: relative;
  height: 100%;
}

.bg-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
</style>

<style>
/* Styles to hide n-input__suffix spacing */
#redirect-url-preview > div.n-input-wrapper > div.n-input__suffix {
  display: none;
}

#submitMessage > div.n-input-wrapper > div.n-input__suffix {
  display: none;
}

/* Also adjust the input padding since suffix is hidden */
#redirect-url-preview > div.n-input-wrapper > div.n-input__input > input.n-input__input-el,
#submitMessage > div.n-input-wrapper > div.n-input__input > input.n-input__input-el {
  padding-right: 12px !important;
}
</style>
